/**
 * OwnerProperties — manage saved properties.
 *
 * Mounted at /property-check/properties. Inline add/edit; no separate detail
 * page in v1 (an inline expand-to-edit row is enough for the small set of
 * properties an owner typically tracks).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import ownerPropertiesService from '../services/ownerPropertiesService';
import LocationInput from '../components/common/LocationInput';

const PROPERTY_TYPES = [
  { value: '', label: 'Not specified' },
  { value: 'residential', label: 'Home / residential' },
  { value: 'airbnb', label: 'Airbnb / short-term rental' },
  { value: 'rental', label: 'Long-term rental' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'plot', label: 'Vacant plot' },
];

const PropertyForm = ({ initial, onSubmit, onCancel, submitting }) => {
  const [label, setLabel] = useState(initial?.label || '');
  const [location, setLocation] = useState(initial?.location || null);
  const [propertyType, setPropertyType] = useState(initial?.propertyType || '');

  const handle = () => {
    if (!label.trim()) return;
    if (!location) return;
    onSubmit({
      label: label.trim(),
      location,
      propertyType: propertyType || undefined,
    });
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Harare house, Bulawayo Airbnb…"
        required
        fullWidth
      />
      <LocationInput
        value={location}
        onChange={setLocation}
        label="Address"
        placeholder="Search for the address"
        required
      />
      <TextField
        select
        label="Property type"
        value={propertyType}
        onChange={(e) => setPropertyType(e.target.value)}
        fullWidth
      >
        {PROPERTY_TYPES.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handle}
          disabled={submitting || !label.trim() || !location}
        >
          {submitting ? <CircularProgress size={20} /> : initial ? 'Save changes' : 'Add property'}
        </Button>
      </Stack>
    </Stack>
  );
};

const OwnerProperties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    try {
      const data = await ownerPropertiesService.list();
      setProperties(data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleAdd = async (payload) => {
    setSubmitting(true);
    try {
      await ownerPropertiesService.create(payload);
      setAddOpen(false);
      await reload();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (payload) => {
    if (!editTarget) return;
    setSubmitting(true);
    try {
      await ownerPropertiesService.update(editTarget.id, payload);
      setEditTarget(null);
      await reload();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (property) => {
    if (!window.confirm(`Archive "${property.label}"? You can restore it later.`)) return;
    try {
      await ownerPropertiesService.archive(property.id);
      await reload();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/property-check/dashboard')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to dashboard
      </Button>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Saved properties
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Properties you've used for verifications. Save details once and re-book in one tap.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add property
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {properties.length === 0 && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            No saved properties yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add a property to make re-booking faster.
          </Typography>
          <Button variant="contained" onClick={() => setAddOpen(true)} startIcon={<AddIcon />}>
            Add your first property
          </Button>
        </Paper>
      )}

      <Stack spacing={2}>
        {properties.map((p) => (
          <Paper key={p.id} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {p.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {p.location?.address || p.location?.formattedAddress || p.location?.city || '—'}
                </Typography>
                {p.propertyType && (
                  <Typography variant="caption" color="text.secondary">
                    {PROPERTY_TYPES.find((t) => t.value === p.propertyType)?.label}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/property-check/book?propertyId=${p.id}`)}
                >
                  Book a check
                </Button>
                <IconButton size="small" onClick={() => setEditTarget(p)} aria-label="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleArchive(p)} aria-label="Archive">
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a property</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <PropertyForm
              onSubmit={handleAdd}
              onCancel={() => setAddOpen(false)}
              submitting={submitting}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit property</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <PropertyForm
              initial={editTarget}
              onSubmit={handleEdit}
              onCancel={() => setEditTarget(null)}
              submitting={submitting}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default OwnerProperties;
